import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import clsx from 'clsx';
import { resolvedApiBaseUrl } from '@api/http';
import { withAccessToken } from '@auth/token';
import type { Recording } from '@api/types';

interface Props {
  recording: Recording;
  cameraName: string;
  onClose: () => void;
}

type PlayerState = 'loading' | 'playing' | 'paused' | 'error';

const SEEK_STEP_SECONDS = 10;

function resolvePlaybackSrc(playbackUrl: string): string {
  if (playbackUrl.startsWith('http://') || playbackUrl.startsWith('https://')) {
    return playbackUrl;
  }
  return withAccessToken(
    `${resolvedApiBaseUrl}${playbackUrl.startsWith('/') ? playbackUrl : `/${playbackUrl}`}`,
  );
}

function formatDuration(totalSeconds: number): string {
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = Math.floor(totalSeconds % 60);
  if (hours > 0) return `${hours}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
  return `${minutes}:${String(seconds).padStart(2, '0')}`;
}

function clipLabel(recording: Recording): string {
  if (recording.fileName) return recording.fileName;
  return recording.name.endsWith('.mp4') ? recording.name : `${recording.name}.mp4`;
}

export function RecordingClipPlayer({ recording, cameraName, onClose }: Props): JSX.Element {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [state, setState] = useState<PlayerState>('loading');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [muted, setMuted] = useState(true);
  const [currentTimeSeconds, setCurrentTimeSeconds] = useState(0);
  const [durationSeconds, setDurationSeconds] = useState(Math.max(1, recording.durationSeconds));
  const [jumpToSecondsInput, setJumpToSecondsInput] = useState('');

  const src = useMemo(() => resolvePlaybackSrc(recording.playbackUrl), [recording.playbackUrl]);
  const label = clipLabel(recording);
  const maxSeekSeconds = Math.max(1, durationSeconds);
  const formattedCurrentTime = formatDuration(currentTimeSeconds);
  const formattedTotalTime = formatDuration(maxSeekSeconds);

  const startPlayback = useCallback(async () => {
    const video = videoRef.current;
    if (!video) return;
    try {
      await video.play();
      setState('playing');
    } catch {
      setState('paused');
    }
  }, []);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return undefined;

    setState('loading');
    setErrorMessage(null);
    setCurrentTimeSeconds(0);
    setDurationSeconds(Math.max(1, recording.durationSeconds));
    setJumpToSecondsInput('');
    video.load();

    const onCanPlay = (): void => {
      void startPlayback();
    };
    const onError = (): void => {
      setState('error');
      setErrorMessage('Could not play this clip. The file may be missing or still recording.');
    };
    const onPlay = (): void => setState('playing');
    const onPause = (): void => setState((s) => (s === 'error' ? s : 'paused'));
    const onTimeUpdate = (): void => {
      setCurrentTimeSeconds(video.currentTime);
    };
    const onDurationChange = (): void => {
      if (Number.isFinite(video.duration) && video.duration > 0) {
        setDurationSeconds(video.duration);
      }
    };
    const onLoadedMetadata = (): void => {
      onDurationChange();
      setCurrentTimeSeconds(video.currentTime);
    };

    video.addEventListener('canplay', onCanPlay);
    video.addEventListener('error', onError);
    video.addEventListener('play', onPlay);
    video.addEventListener('pause', onPause);
    video.addEventListener('timeupdate', onTimeUpdate);
    video.addEventListener('durationchange', onDurationChange);
    video.addEventListener('loadedmetadata', onLoadedMetadata);

    return () => {
      video.removeEventListener('canplay', onCanPlay);
      video.removeEventListener('error', onError);
      video.removeEventListener('play', onPlay);
      video.removeEventListener('pause', onPause);
      video.removeEventListener('timeupdate', onTimeUpdate);
      video.removeEventListener('durationchange', onDurationChange);
      video.removeEventListener('loadedmetadata', onLoadedMetadata);
      video.pause();
    };
  }, [src, startPlayback, recording.durationSeconds]);

  const togglePlayPause = useCallback(() => {
    const video = videoRef.current;
    if (!video || state === 'error') return;
    if (video.paused) {
      void video.play();
    } else {
      video.pause();
    }
  }, [state]);

  const seekTo = useCallback((nextSeconds: number) => {
    const video = videoRef.current;
    if (!video || state === 'error') return;
    const bounded = Math.min(maxSeekSeconds, Math.max(0, nextSeconds));
    video.currentTime = bounded;
    setCurrentTimeSeconds(bounded);
  }, [maxSeekSeconds, state]);

  const seekBy = useCallback((deltaSeconds: number) => {
    seekTo(currentTimeSeconds + deltaSeconds);
  }, [currentTimeSeconds, seekTo]);

  return (
    <section
      className="mx-6 mb-4 overflow-hidden rounded-lg border shadow-card"
      style={{
        borderColor: 'var(--bui-border)',
        backgroundColor: 'var(--bui-surface)',
      }}
      aria-label={`Playing ${label}`}
    >
      <header
        className="flex flex-wrap items-center justify-between gap-2 border-b px-4 py-2"
        style={{ borderColor: 'var(--bui-divider)' }}
      >
        <div className="min-w-0">
          <p className="truncate text-sm font-medium" style={{ color: 'var(--bui-text-primary)' }}>
            {cameraName}
          </p>
          <p className="truncate font-mono text-xs" style={{ color: 'var(--bui-text-secondary)' }} title={label}>
            {label}
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <span className="text-xs tabular-nums" style={{ color: 'var(--bui-text-secondary)' }}>
            {formatDuration(recording.durationSeconds)}
          </span>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border px-2.5 py-1 text-xs font-medium transition-colors hover:bg-[var(--bui-surface-hover)] focus-visible:outline-none focus-visible:ring-2"
            style={{
              borderColor: 'var(--bui-border)',
              color: 'var(--bui-text-primary)',
            }}
          >
            Close
          </button>
        </div>
      </header>

      <div className="relative aspect-video w-full bg-black">
        <video
          ref={videoRef}
          key={src}
          src={src}
          className={clsx(
            'h-full w-full object-contain transition-opacity',
            state === 'playing' || state === 'paused' ? 'opacity-100' : 'opacity-0',
          )}
          playsInline
          muted={muted}
          controls={false}
          preload="auto"
        />

        {state === 'loading' && (
          <div className="absolute inset-0 flex items-center justify-center text-sm text-white/80">
            Loading clip…
          </div>
        )}

        {state === 'error' && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 px-4 text-center text-sm text-white/90">
            <p>{errorMessage}</p>
            <button
              type="button"
              onClick={() => {
                const video = videoRef.current;
                if (!video) return;
                setState('loading');
                setErrorMessage(null);
                video.load();
              }}
              className="rounded-md bg-white/15 px-3 py-1 text-xs font-medium hover:bg-white/25"
            >
              Retry
            </button>
          </div>
        )}

        <div className="absolute inset-x-0 bottom-0 flex items-center justify-between gap-2 bg-gradient-to-t from-black/80 to-transparent px-3 pb-3 pt-8">
          <div className="flex min-w-0 flex-1 flex-col gap-2">
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => seekBy(-SEEK_STEP_SECONDS)}
                disabled={state === 'error' || state === 'loading'}
                className="rounded-md bg-white/15 px-2.5 py-1.5 text-xs font-medium text-white hover:bg-white/25 disabled:opacity-50"
                title={`Back ${SEEK_STEP_SECONDS} seconds`}
              >
                -{SEEK_STEP_SECONDS}s
              </button>
              <input
                type="range"
                min={0}
                max={maxSeekSeconds}
                step={0.1}
                value={Math.min(maxSeekSeconds, currentTimeSeconds)}
                onChange={(e) => seekTo(Number(e.target.value))}
                disabled={state === 'error' || state === 'loading'}
                className="w-full accent-[var(--bui-accent)]"
                aria-label="Seek recording"
              />
              <button
                type="button"
                onClick={() => seekBy(SEEK_STEP_SECONDS)}
                disabled={state === 'error' || state === 'loading'}
                className="rounded-md bg-white/15 px-2.5 py-1.5 text-xs font-medium text-white hover:bg-white/25 disabled:opacity-50"
                title={`Forward ${SEEK_STEP_SECONDS} seconds`}
              >
                +{SEEK_STEP_SECONDS}s
              </button>
            </div>
            <div className="flex items-center justify-between gap-2">
              <span className="text-[11px] tabular-nums text-white/90">
                {formattedCurrentTime} / {formattedTotalTime}
              </span>
              <div className="flex items-center gap-2">
                <label className="flex items-center gap-1 text-[11px] text-white/85">
                  <span>Jump</span>
                  <input
                    type="number"
                    min={0}
                    max={Math.floor(maxSeekSeconds)}
                    step={1}
                    value={jumpToSecondsInput}
                    onChange={(e) => setJumpToSecondsInput(e.target.value)}
                    onKeyDown={(event) => {
                      if (event.key !== 'Enter') return;
                      const parsed = Number(jumpToSecondsInput);
                      if (!Number.isFinite(parsed)) return;
                      seekTo(parsed);
                    }}
                    className="w-16 rounded border border-white/25 bg-black/30 px-1.5 py-0.5 text-[11px] text-white focus-visible:outline-none focus-visible:ring-2"
                    aria-label="Jump to second"
                  />
                  <span>s</span>
                </label>
                <button
                  type="button"
                  onClick={() => {
                    const parsed = Number(jumpToSecondsInput);
                    if (!Number.isFinite(parsed)) return;
                    seekTo(parsed);
                  }}
                  disabled={state === 'error' || state === 'loading'}
                  className="rounded-md bg-white/15 px-2.5 py-1.5 text-xs font-medium text-white hover:bg-white/25 disabled:opacity-50"
                >
                  Go
                </button>
                <button
                  type="button"
                  onClick={togglePlayPause}
                  disabled={state === 'error' || state === 'loading'}
                  className="rounded-md bg-white/15 px-3 py-1.5 text-xs font-semibold text-white hover:bg-white/25 disabled:opacity-50"
                >
                  {state === 'playing' ? 'Pause' : 'Play'}
                </button>
                <button
                  type="button"
                  onClick={() => setMuted((m) => !m)}
                  className="rounded-md bg-white/15 px-3 py-1.5 text-xs font-medium text-white hover:bg-white/25"
                >
                  {muted ? 'Unmute' : 'Mute'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
